import { COOKIES_ACCEPTANCE_LOCAL_STORAGE_KEY, GDPR_COUNTRY_CODES } from '@/constants/constants';
import { initializeAppInsights } from '@/lib/appInsights';
import { initializeClarity } from '@/lib/clarity';
import { initializeSentry } from '@/lib/sentry';

interface CookieConsent {
  accepted: boolean | null;
}

export class CookieService {
  private consent: CookieConsent = { accepted: null };

  thirdPartyServicesInitialized = false;

  constructor() {
    this.loadConsentFromLocalStorage();
  }

  private loadConsentFromLocalStorage(): void {
    try {
      const consentStatus = localStorage.getItem(COOKIES_ACCEPTANCE_LOCAL_STORAGE_KEY);

      if (consentStatus === 'accepted') {
        this.accept();
      } else if (consentStatus === 'declined') {
        this.decline();
      }
    } catch (error) {
      console.error('Error loading cookie consent from storage:', error);
    }
  }

  private saveConsentToLocalStorage(): void {
    try {
      localStorage.setItem(COOKIES_ACCEPTANCE_LOCAL_STORAGE_KEY, this.consent.accepted ? 'accepted' : 'declined');
    } catch (error) {
      console.error('Error saving cookie consent to storage:', error);
    }
  }

  public accept(): void {
    this.consent.accepted = true;
    this.saveConsentToLocalStorage();

    if (!this.thirdPartyServicesInitialized) {
      this.initializeThirdPartyServices();
      this.thirdPartyServicesInitialized = true;
    }
  }

  public decline(): void {
    this.consent.accepted = false;
    this.saveConsentToLocalStorage();
  }

  public hasConsent(): CookieConsent['accepted'] {
    return this.consent.accepted;
  }

  private initializeThirdPartyServices(): void {
    if (this.consent.accepted) {
      console.log('Initializing third-party services...');
      initializeAppInsights();
      initializeClarity();
      initializeSentry();
    }
  }

  public isGdprCountry(countryCode: string): boolean {
    return GDPR_COUNTRY_CODES.has(countryCode.toUpperCase());
  }

  public handleUserLocation(countryCode: string): void {
    // Auto-accept cookies for non-GDPR users if consent is not already set
    if (!this.isGdprCountry(countryCode) && this.hasConsent() === null) {
      this.accept();
    }
  }
}

export const cookieService = new CookieService();
